import { useState, useEffect } from 'react'
import axios from '../utils/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Users, Gift, Building2, GitFork, Ticket, TrendingUp } from 'lucide-react'

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalGifts: 0,
    totalSponsors: 0,
    pendingSubmissions: 0,
    pendingRedemptions: 0,
    totalContributions: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const fetchStats = async () => {
      try {
        setLoading(true)
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        
        // In a real app, you'd have a dedicated stats endpoint
        // For now, we'll fetch from existing endpoints
        const [usersRes, giftsRes, sponsorsRes, submissionsRes] = await Promise.all([
          axios.get('/admin/users', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/admin/gifts', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/admin/sponsors', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/admin/submissions', {
            headers: { Authorization: `Bearer ${token}` },
            params: { status: 'pending' }
          })
        ])

        if (cancelled) return

        const sponsorData = Array.isArray(sponsorsRes.data) ? sponsorsRes.data : (sponsorsRes.data.sponsors || [])
        
        setStats({
          totalUsers: usersRes.data.users?.length || 0,
          totalGifts: giftsRes.data.gifts?.length || 0,
          totalSponsors: sponsorData.length || 0,
          pendingSubmissions: submissionsRes.data.submissions?.length || 0,
          pendingRedemptions: 0, // Will be implemented
          totalContributions: 0 // Will be implemented
        })
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching stats:', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchStats()
    return () => { cancelled = true }
  }, [])

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      description: 'Registered users',
      color: 'text-blue-500'
    },
    {
      title: 'Gift Items',
      value: stats.totalGifts,
      icon: Gift,
      description: 'Available in store',
      color: 'text-green-500'
    },
    {
      title: 'Sponsors',
      value: stats.totalSponsors,
      icon: Building2,
      description: 'Active sponsors',
      color: 'text-purple-500'
    },
    {
      title: 'Pending Submissions',
      value: stats.pendingSubmissions,
      icon: GitFork,
      description: 'Awaiting review',
      color: 'text-orange-500'
    },
    {
      title: 'Pending Redemptions',
      value: stats.pendingRedemptions,
      icon: Ticket,
      description: 'Awaiting approval',
      color: 'text-red-500'
    },
    {
      title: 'Total Contributions',
      value: stats.totalContributions,
      icon: TrendingUp,
      description: 'All time',
      color: 'text-indigo-500'
    }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overview of your FOSSIT platform</p>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Loading statistics...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {statCards.map((stat) => {
            const Icon = stat.icon
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <a
              href="/admin/users"
              className="p-4 border rounded-lg hover:bg-accent transition-colors"
            >
              <h3 className="font-semibold">Manage Users</h3>
              <p className="text-sm text-muted-foreground mt-1">
                View, edit, and manage user accounts
              </p>
            </a>
            <a
              href="/admin/store"
              className="p-4 border rounded-lg hover:bg-accent transition-colors"
            >
              <h3 className="font-semibold">Manage Store</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add, edit, and manage gift items
              </p>
            </a>
            <a
              href="/admin/sponsors"
              className="p-4 border rounded-lg hover:bg-accent transition-colors"
            >
              <h3 className="font-semibold">Manage Sponsors</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add and manage platform sponsors
              </p>
            </a>
            <a
              href="/admin/submissions"
              className="p-4 border rounded-lg hover:bg-accent transition-colors"
            >
              <h3 className="font-semibold">Review Submissions</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Approve or reject repository submissions
              </p>
            </a>
            <a
              href="/admin/redemptions"
              className="p-4 border rounded-lg hover:bg-accent transition-colors"
            >
              <h3 className="font-semibold">Manage Redemptions</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Process gift redemption requests
              </p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
